import type { ChangeRow } from "../OperationInspectorPersist.js";

/**
 * ADR-0102 — unified rebuild banner (carry-forward from ADR-0101 paused
 * history `ddae78c`). Renamed from ADR-0100's `PersistDriftBanner`; absorbs
 * the role of ADR-0095's red "INIT" banner so one component covers every
 * "this persist will rebuild on the next run" surface.
 *
 * Renders when the persist's PDO is in the **Rebuild Required** state for
 * any combination of reasons:
 *   - `drift`: spec config differs from the PDO's last-init snapshot
 *   - `operator`: `pdo.current_run_mode === 'initialize'` on a keyed PDO that
 *     already has versions — user toggled "Re-initialize on next run" (or hit
 *     the legacy `/watermark/mode` PATCH). Watermark binding is irrelevant;
 *     the mode lives on the PDO row in every case (migration 159).
 *   - `first_run`: the PDO has no versions yet
 *
 * Visibility logic + hide-while-running guard live in the parent inspector;
 * if this component renders, it shows.
 *
 * The single primary CTA `Re-initialize` calls the execute-persist endpoint.
 * The parent inspector handles destructive confirm via `useConfirm()` for the
 * drift/operator paths (typed-confirm is skipped for first_run since there's
 * no data to wipe).
 */

export type RebuildReason = "drift" | "operator" | "first_run";

const REASON_LABELS: Record<RebuildReason, string> = {
  drift: "Config drift detected",
  operator: "Operator chose to rebuild",
  first_run: "Never initialized",
};

export function PersistRebuildBanner({
  reasons,
  changes,
  onReinitialize,
  disabled,
}: {
  /** Active reasons. Multiple may be true simultaneously (drift + operator). */
  reasons: RebuildReason[];
  /** ADR-0100 drift change rows. Empty when drift is not among the reasons. */
  changes: ChangeRow[];
  onReinitialize: () => void;
  disabled: boolean;
}) {
  // Defensive: render nothing when no reasons supplied. The parent
  // inspector's visibility guard should normally prevent this.
  if (reasons.length === 0) return null;
  const includesDrift = reasons.includes("drift");
  const includesOperator = reasons.includes("operator");
  const onlyFirstRun = reasons.length === 1 && reasons[0] === "first_run";

  const explanation = includesDrift
    ? "The configuration on this node differs from the data store. Re-initialize to apply:"
    : onlyFirstRun
      ? "This PDO has no versions yet. Re-initialize to perform the first load."
      : "This PDO will rebuild from scratch on the next run.";

  return (
    <div className="card" style={{ borderColor: "var(--danger-text)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>Rebuild required</span>
        {reasons.map((r) => (
          <span key={r} className="pill pillWarn" style={{ fontSize: 11 }}>
            {REASON_LABELS[r]}
          </span>
        ))}
      </div>
      <div className="help" style={{ marginBottom: 10 }}>{explanation}</div>
      {includesDrift && changes.length > 0 ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          {changes.map((c) => (
            <div key={c.field} style={{ display: "grid", gap: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{c.label}</div>
              <div className="mono" style={{ fontSize: 12 }}>
                <span className="muted">{c.before}</span>
                {" → "}
                <span>{c.after}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <button
        className="btn btnSm btnDanger"
        type="button"
        onClick={onReinitialize}
        disabled={disabled}
      >
        Re-initialize
      </button>
      <div className="help muted" style={{ marginTop: 8 }}>
        {includesDrift || includesOperator
          ? "Re-initialize wipes all versions and reloads from the upstream with the new configuration."
          : "Re-initialize runs the first INIT chain and writes the PDO's first version."}
      </div>
    </div>
  );
}
